import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera, RefreshCw, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ds/Button';
import { Card } from '../ds/Card';
import { useUploadWorkflow } from '../../hooks/useUploadWorkflow';
import { ErrorBoundary } from '../ErrorBoundary';

// Humorous loading messages to keep users entertained
const LOADING_MESSAGES = [
  "Reading TrackMan's mind...",
  "Decoding launch monitor hieroglyphics...",
  "Teaching AI to appreciate your swing...",
  "Calculating ball flight physics...",
  "Consulting with the golf gods...",
  "Turning pixels into yardage...",
  "Analyzing your TrackMan masterpiece...",
  "Converting screenshot to data magic...",
  "Asking the launch monitor nicely...",
  "Crunching numbers faster than a 7-iron...",
];

/**
 * OCRUploader - Isolated OCR/Image upload component
 *
 * Features:
 * - Drag and drop support for images
 * - Progress indicator
 * - Error handling with retry
 * - Image preview
 * - Cancellation support
 */
export function OCRUploader({ category, clubType, dexterity, onUploadComplete }) {
  const {
    stage,
    error,
    progress,
    data,
    upload,
    cancel,
    reset,
    retry,
    isUploading,
    hasError,
    isComplete,
  } = useUploadWorkflow();

  const [preview, setPreview] = React.useState(null);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const uploadTimeoutRef = React.useRef(null);

  // Rotate loading messages while uploading
  useEffect(() => {
    if (!isUploading) return;

    let messageIndex = 0;
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[messageIndex]);
    }, 2500); // Change message every 2.5 seconds

    return () => clearInterval(interval);
  }, [isUploading]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // Safety timeout to prevent infinite loading (70 seconds - longer than backend timeout)
    uploadTimeoutRef.current = setTimeout(() => {
      console.error('OCR upload exceeded 70-second safety timeout');
      reset();
    }, 70000);

    try {
      const result = await upload(file, '/analyze/ocr', {
        mode: 'matrix',
        category,
        club_type: clubType,
        dexterity,
        playing_conditions: false,
      });

      clearTimeout(uploadTimeoutRef.current);

      if (onUploadComplete) {
        onUploadComplete(result);
      }
    } catch (err) {
      // Error is handled by useUploadWorkflow
      clearTimeout(uploadTimeoutRef.current);
      console.error('OCR upload failed:', err);
    }
  }, [upload, category, clubType, dexterity, onUploadComplete, reset]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    multiple: false,
    disabled: isUploading,
  });

  const handleReset = useCallback(() => {
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
    }
    reset();
    setPreview(null);
  }, [reset]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <Card padding="p-6">
        {/* Upload Area */}
        {!isComplete && !hasError && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />

            {isUploading ? (
              <>
                {preview && (
                  <img
                    src={preview}
                    alt="Upload preview"
                    className="mx-auto max-w-xs max-h-48 rounded-lg mb-4 opacity-50"
                  />
                )}
                <RefreshCw className="animate-spin mx-auto text-primary w-12 h-12" />
                <p className="mt-4 text-white font-medium">{loadingMessage}</p>
                {progress > 0 && (
                  <div className="mt-4 max-w-xs mx-auto">
                    <div className="bg-surface rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-text-muted mt-2">{progress}%</p>
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    cancel();
                  }}
                  className="mt-4 text-sm text-text-muted hover:text-text-primary"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <Camera className="mx-auto text-text-muted w-12 h-12" />
                <p className="mt-4 text-text-primary font-medium">
                  {isDragActive ? 'Drop image here' : 'Drop launch monitor screenshot here'}
                </p>
                <p className="text-xs text-text-muted mt-2">
                  Supports JPG, PNG, WebP (max 10MB)
                </p>
              </>
            )}
          </div>
        )}

        {/* Success State - Minimized */}
        {isComplete && (
          <div className="bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-3">
            <CheckCircle2 className="text-success flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-success text-sm font-medium">
                Image processed - {data?.shots?.length || 0} shot{data?.shots?.length !== 1 ? 's' : ''} extracted
              </p>
            </div>
            <button
              onClick={handleReset}
              className="text-text-muted hover:text-text-primary"
              title="Upload different image"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Error State */}
        {hasError && (
          <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-status-error flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-status-error font-medium">OCR processing failed</p>
                <p className="text-sm text-text-secondary mt-1">{error}</p>
                {preview && (
                  <img
                    src={preview}
                    alt="Failed upload"
                    className="mt-3 max-w-xs rounded border border-status-error/30 opacity-50"
                  />
                )}
                <button
                  onClick={handleReset}
                  className="text-sm text-primary hover:underline mt-2"
                >
                  Try another image
                </button>
              </div>
              <button
                onClick={handleReset}
                className="text-text-muted hover:text-text-primary"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
      </Card>
    </ErrorBoundary>
  );
}

export default OCRUploader;
